import React from 'react';
import PropTypes from 'prop-types';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/border.css';
import 'tippy.js/dist/tippy.css';
import { injectIntl } from 'react-intl';
import useWindowWidth from '../../utils/useWindowWidthHook';
import infoIcon from '../../assets/icons/info.svg';
import './InfoBubble.scss';
import s from './InfoBubble.module.scss';

/**
 * Tippy plugin hide on esc
 * https://atomiks.github.io/tippyjs/v6/plugins/
 */

const hideOnEsc = {
  name: 'hideOnEsc',
  defaultValue: true,
  fn({ hide }) {
    function onKeyDown(event) {
      if (event.keyCode === 27) {
        hide();
      }
    }

    return {
      onShow() {
        document.addEventListener('keydown', onKeyDown);
      },
      onHide() {
        document.removeEventListener('keydown', onKeyDown);
      },
    };
  },
};

const setDescribedBy = {
  name: 'setDescribedBy',
  defaultValue: true,
  fn() {
    return {
      onShown({ reference, popper }) {
        reference.setAttribute('aria-describedby', popper.id);
      },
      onHide({ reference }) {
        reference.removeAttribute('aria-describedby');
      },
    };
  },
};

/**
 * Renders an info icon with a tooltip. Children will be rendered in the tooltip.
 */
function InfoBubble({ children, tippyProps, iconAltText, intl }) {
  const windowWidth = useWindowWidth();
  return (
    <Tippy
      content={children}
      theme="verbund"
      placement={windowWidth >= '768' ? 'right' : 'top'}
      trigger="mouseenter focus click"
      plugins={[hideOnEsc, setDescribedBy]}
      interactive
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...tippyProps}
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <div className={s.imageWrapper} tabIndex={0}>
        <img
          src={infoIcon}
          alt={iconAltText || intl.formatMessage({ id: 'alt.iconInfo' })}
        />
      </div>
    </Tippy>
  );
}

InfoBubble.defaultProps = {
  tippyProps: null,
  iconAltText: '',
};

InfoBubble.propTypes = {
  /** Content of the tooltip box e.g. the text you wish to display */
  children: PropTypes.node.isRequired,
  /** Props to pass directly to Tippy. For a list of props see https://atomiks.github.io/tippyjs/ */
  // eslint-disable-next-line react/forbid-prop-types
  tippyProps: PropTypes.object,
  /**  Set a custom alt text for the info icon */
  iconAltText: PropTypes.string,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
};

export default injectIntl(InfoBubble);
